import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertCircle, MailWarning } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { V2 } from '../v2/shared';

interface LoginFormData { email: string; password: string }
interface LoginError { message: string; unconfirmed?: boolean }

const toLoginError = (error: any): LoginError => {
  if (!error.response) return { message: 'No pudimos conectarnos. Revisá tu conexión a internet.' };
  const { status, data } = error.response;
  if (status === 403 && data?.code === 'EMAIL_NOT_CONFIRMED') {
    return { message: 'Todavía no confirmaste tu email. Revisá tu casilla para activar tu cuenta.', unconfirmed: true };
  }
  if (status === 401 || status === 404) return { message: 'El email o la contraseña no son correctos.' };
  if (status === 429) return { message: 'Demasiados intentos. Esperá unos minutos y probá de nuevo.' };
  if (status >= 500) return { message: 'Hubo un problema de nuestro lado. Probá de nuevo en unos minutos.' };
  return { message: data?.error || 'Ocurrió un error inesperado.' };
};

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<LoginError | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    if (loading) return;
    setLoading(true);
    setLoginError(null);
    try {
      const user = await login(data.email, data.password);
      navigate(user.role === 'admin' ? '/admin' : user.role === 'professional' ? '/dashboard' : '/');
    } catch (error: any) {
      setLoginError(toLoginError(error));
      setLoading(false);
    }
  };

  return (
    <V2>
      <main className="auth">
        <div className="auth-card">
          <div className="head">
            <h1>Ingresá a tu cuenta</h1>
            <p>Pacientes, profesionales y administración.</p>
          </div>
          <form className="panel" onSubmit={handleSubmit(onSubmit)} noValidate>
            {loginError && (
              <div className="alert bad" role="alert">
                {loginError.unconfirmed ? <MailWarning size={18} style={{ flexShrink: 0 }} /> : <AlertCircle size={18} style={{ flexShrink: 0 }} />}
                <div>
                  {loginError.message}
                  {loginError.unconfirmed && <> <Link to="/confirm-email" style={{ fontWeight: 700, textDecoration: 'underline' }}>Reenviar confirmación</Link></>}
                </div>
              </div>
            )}
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" autoComplete="email" inputMode="email" className={`in ${errors.email ? 'bad' : ''}`}
                {...register('email', { required: 'Ingresá tu email', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Revisá el formato del email' } })} />
              {errors.email && <p className="err" role="alert">{errors.email.message}</p>}
            </div>
            <div className="field">
              <label htmlFor="password">Contraseña</label>
              <div className="pw">
                <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" className={`in ${errors.password ? 'bad' : ''}`}
                  {...register('password', { required: 'Ingresá tu contraseña' })} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="err" role="alert">{errors.password.message}</p>}
            </div>
            <button type="submit" className="btn2 btn2-cta" style={{ width: '100%' }} disabled={loading}>
              {loading ? <><Loader2 size={18} className="animate-spin" /> Ingresando…</> : 'Ingresar'}
            </button>
          </form>
          <p className="auth-alt">¿Sos paciente y no tenés cuenta? <Link to="/register">Creala acá</Link></p>
        </div>
      </main>
    </V2>
  );
};

export default Login;
